import type { CmtsAuthTokenSet } from "../api/cmtsHttpClient.js";
import type { CmtsResolvedRuntimeMode } from "../../../app/appConfig.js";
import { normalizeCmtsRoles } from "./normalizeCmtsRoles.js";

export type CmtsStoredSession = CmtsAuthTokenSet & {
  userId?: string | undefined;
  tenantId?: string | undefined;
  tenantCode?: string | undefined;
  tenantName?: string | undefined;
  organizationUnitId?: string | undefined;
  organizationUnitIds?: readonly string[] | undefined;
  displayName?: string | undefined;
  email?: string | undefined;
  persona?: string | undefined;
  permissions?: readonly string[] | undefined;
  roles?: readonly string[] | undefined;
  roleIds?: readonly string[] | undefined;
  permissionCodes?: readonly string[] | undefined;
  departmentIds?: readonly string[] | undefined;
  businessUnitIds?: readonly string[] | undefined;
  siteIds?: readonly string[] | undefined;
  customerIds?: readonly string[] | undefined;
  supplierIds?: readonly string[] | undefined;
  isExternalUser?: boolean | undefined;
  externalPartyType?: "CUSTOMER" | "SUPPLIER" | undefined;
  confidentialityClearance?: "PUBLIC" | "INTERNAL" | "CONFIDENTIAL" | "RESTRICTED" | undefined;
  runtimeMode?: CmtsResolvedRuntimeMode | undefined;
};

const STORAGE_KEY = "swot.cmts.session.v1";

type CmtsStorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function defaultStorage(): CmtsStorageLike | null {
  try {
    return typeof globalThis !== "undefined" && "localStorage" in globalThis ? globalThis.localStorage : null;
  } catch (error: unknown) {
    void error;
    return null;
  }
}

export function readCmtsSession(storage: CmtsStorageLike | null = defaultStorage()): CmtsStoredSession | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CmtsStoredSession;
    const roles = normalizeCmtsRoles([...(parsed.roles ?? []), ...(parsed.roleIds ?? [])]);
    return { ...parsed, roles, roleIds: roles };
  } catch (error: unknown) {
    void error;
    return null;
  }
}

export function writeCmtsSession(session: CmtsStoredSession, storage: CmtsStorageLike | null = defaultStorage()): void {
  if (!storage) return;
  const roles = normalizeCmtsRoles([...(session.roles ?? []), ...(session.roleIds ?? [])]);
  storage.setItem(STORAGE_KEY, JSON.stringify({ ...session, roles, roleIds: roles }));
}

export function mergeCmtsSession(patch: CmtsStoredSession, storage: CmtsStorageLike | null = defaultStorage()): void {
  writeCmtsSession({ ...(readCmtsSession(storage) ?? {}), ...patch }, storage);
}

export function clearCmtsSession(storage: CmtsStorageLike | null = defaultStorage()): void {
  if (!storage) return;
  storage.removeItem(STORAGE_KEY);
}

export function getStoredAccessToken(): string | undefined { return readCmtsSession()?.accessToken; }
export function getStoredRefreshToken(): string | undefined { return readCmtsSession()?.refreshToken; }
export function getStoredTenantId(): string | undefined { return readCmtsSession()?.tenantId; }
export function getStoredTenantCode(): string | undefined { return readCmtsSession()?.tenantCode; }
export function getStoredRuntimeMode(): CmtsResolvedRuntimeMode | undefined { return readCmtsSession()?.runtimeMode; }
export function hasCmtsSession(): boolean { return Boolean(readCmtsSession()?.accessToken && readCmtsSession()?.userId); }
